'use client';

import { useCallback } from 'react';

import { Menu } from '@base-ui/react/menu';
import Link from 'next/link';

import { SIGN_IN_HREF } from './nav-data';

import { trackButtonClick } from '@/lib/analytics/track';
import { useHasActiveBuyerOffers } from '@/lib/auth/buyer-offers';
import { useLeads } from '@/lib/auth/leads';
import { useSessionExtras } from '@/lib/auth/session-extras';
import { initialsFor, signOut, useUserSession } from '@/lib/auth/user-session';
import { opendoorUrl } from '@/lib/urls';

const ACCOUNT_HREF = opendoorUrl('/account');
const CLOSING_DASHBOARD_HREF = opendoorUrl('/closing-dashboard');
const DASHBOARD_HREF = opendoorUrl('/dashboard');
const PROPERTIES_HREF = opendoorUrl('/properties');
const HELP_CENTER_HREF = 'https://help.opendoor.com';
const MAX_LEADS_IN_MENU = 3;

const signInLinkClass =
  'rounded-full px-4 py-2 text-base font-medium text-ink transition hover:bg-state-hover';

const iconlessMenuItemClass =
  'flex w-full cursor-pointer items-center rounded-xl py-2 pr-6 pl-[30px] text-left text-base leading-6 whitespace-nowrap text-ink outline-none transition data-[highlighted]:bg-state-hover';

const iconMenuItemClass =
  'flex w-full cursor-pointer items-center gap-2 rounded-xl py-2 pr-6 pl-2 text-left text-base leading-6 whitespace-nowrap text-ink outline-none transition data-[highlighted]:bg-state-hover';

function Divider() {
  return <div className="my-1 h-px bg-line-soft" />;
}

function HomeIcon() {
  return (
    <svg
      aria-hidden
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 11l9-8 9 8" />
      <path d="M5 10v10a1 1 0 0 0 1 1h4v-7h4v7h4a1 1 0 0 0 1-1V10" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg
      aria-hidden
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 8V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2v-2" />
      <path d="M15 12H3" />
      <path d="M6 9l-3 3 3 3" />
    </svg>
  );
}

export function HeaderAccount() {
  const auth = useUserSession();
  const leads = useLeads();
  const extras = useSessionExtras();
  const hasActiveBuyerOffers = useHasActiveBuyerOffers();

  const handleAvatarClick = useCallback(() => {
    trackButtonClick('user-account-menu-click');
  }, []);
  const handleSignInClick = useCallback(() => {
    trackButtonClick('account-section-click');
  }, []);
  const handleLeadClick = useCallback(() => {
    trackButtonClick('account-lead-click');
  }, []);
  const handleMoreClick = useCallback(() => {
    trackButtonClick('account-leads-more');
  }, []);
  const handleClosingDashClick = useCallback(() => {
    trackButtonClick('account-closing-dashboard');
  }, []);
  const handleDashboardClick = useCallback(() => {
    trackButtonClick('dashboard-link');
  }, []);
  const handleAccountClick = useCallback(() => {
    trackButtonClick('account-link');
  }, []);
  const handleHelpCenterClick = useCallback(() => {
    trackButtonClick('account-help-center');
  }, []);
  const handleSignOut = useCallback(() => {
    trackButtonClick('account-signout');
    void signOut();
  }, []);

  if (auth.kind !== 'authenticated') {
    return (
      <Link
        href={SIGN_IN_HREF}
        prefetch={false}
        className={signInLinkClass}
        onClick={handleSignInClick}
      >
        Sign in
      </Link>
    );
  }

  const initials = initialsFor(auth.human);
  const visibleLeads = leads.slice(0, MAX_LEADS_IN_MENU);
  const hasLeadsOverflow = leads.length > MAX_LEADS_IN_MENU;
  const hasLeadsSection = visibleLeads.length > 0;

  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label="Your account menu"
        data-header-account-trigger
        onClick={handleAvatarClick}
        className="inline-flex h-8 w-8 cursor-pointer items-center justify-center border-0 bg-transparent p-0 outline-none transition hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2"
      >
        <span
          aria-hidden
          data-header-account-avatar
          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-ink text-xs font-medium text-canvas"
        >
          {initials}
        </span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={12} align="end" className="z-50">
          <Menu.Popup className="w-64 rounded-2xl border border-line-soft bg-canvas p-[5px] shadow-lg">
            {visibleLeads.map((lead) => (
              <Menu.Item
                key={lead.id}
                className={iconlessMenuItemClass}
                render={<Link href={lead.url} prefetch={false} onClick={handleLeadClick} />}
              >
                <span>
                  {lead.street ?? 'Your property'}
                  {lead.readableOfferState ? ` (${lead.readableOfferState})` : ''}
                </span>
              </Menu.Item>
            ))}
            {hasLeadsOverflow && (
              <Menu.Item
                className={iconlessMenuItemClass}
                render={<Link href={PROPERTIES_HREF} prefetch={false} onClick={handleMoreClick} />}
              >
                <span>More</span>
              </Menu.Item>
            )}
            {hasLeadsSection && <Divider />}
            {extras.hasResaleClosingDashboard && (
              <Menu.Item
                className={iconlessMenuItemClass}
                render={
                  <Link
                    href={CLOSING_DASHBOARD_HREF}
                    prefetch={false}
                    onClick={handleClosingDashClick}
                  />
                }
              >
                <span>Closing dash</span>
              </Menu.Item>
            )}
            {extras.hasResaleClosingDashboard && <Divider />}
            {hasActiveBuyerOffers && (
              <Menu.Item
                className={iconMenuItemClass}
                render={
                  <Link href={DASHBOARD_HREF} prefetch={false} onClick={handleDashboardClick} />
                }
              >
                <HomeIcon />
                <span>Dashboard</span>
              </Menu.Item>
            )}
            <Menu.Item
              className={iconlessMenuItemClass}
              render={<Link href={ACCOUNT_HREF} prefetch={false} onClick={handleAccountClick} />}
            >
              <span>Account</span>
            </Menu.Item>
            <Divider />
            <Menu.Item
              className={iconlessMenuItemClass}
              render={
                <Link href={HELP_CENTER_HREF} prefetch={false} onClick={handleHelpCenterClick} />
              }
            >
              <span>Help center</span>
            </Menu.Item>
            <Menu.Item className={iconMenuItemClass} onClick={handleSignOut}>
              <LogoutIcon />
              <span>Sign out</span>
            </Menu.Item>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
